import { Outlet, RouteObject } from "react-router";
import { Redirect } from "src/router/helper/Redirect";

export const MeasurementModalRoutes: RouteObject[] = [
  {
    path: "get",
    async lazy() {
      const { GetMeasurementsModal } = await import("jobs/Job/MeasurementsTab/GetMeasurementsModal");
      return { element: <GetMeasurementsModal /> };
    },
    children: [
      {
        path: "eagleview_roofing",
        async loader(args) {
          const { eagleviewLoader } = await import("src/router/loaders/jobs/Measurements");
          return eagleviewLoader(args);
        },
        async lazy() {
          const { EagleviewOrderingModal } =
            await import("jobs/Job/MeasurementsTab/GetMeasurementsModal/EagleviewOrderingModal");
          return { element: <EagleviewOrderingModal /> };
        },
      },
      {
        path: "quick_measure_roofing",
        async lazy() {
          const { QuickMeasureModal } = await import("jobs/Job/MeasurementsTab/GetMeasurementsModal/QuickMeasureModal");
          return { element: <QuickMeasureModal /> };
        },
      },
      {
        path: "hover",
        async loader(args) {
          const { hoverImportLoader } = await import("src/router/loaders/jobs/HoverImport");
          return hoverImportLoader(args);
        },
        async lazy() {
          const { HoverImportModal } = await import("jobs/Job/MeasurementsTab/GetMeasurementsModal/HoverImportModal");
          return { element: <HoverImportModal /> };
        },
      },
      {
        path: "roof_scope_roofing",
        async loader(args) {
          const { roofScopeImportLoader } = await import("src/router/loaders/jobs/RoofScopeImport");
          return roofScopeImportLoader(args);
        },
        async lazy() {
          const { RoofScopeModal } = await import("jobs/Job/MeasurementsTab/GetMeasurementsModal/RoofScopeModal");
          return { element: <RoofScopeModal /> };
        },
      },
    ],
  },
  {
    path: "rapid_area",
    async loader(args) {
      const { rapidAreaLoader } = await import("src/router/loaders/jobs/Measurements");
      return rapidAreaLoader(args);
    },
    async lazy() {
      const { RapidAreaModal } = await import("jobs/Job/MeasurementsTab/RapidAreaModal");
      return { element: <RapidAreaModal /> };
    },
  },
  {
    path: "add/:type",
    async lazy() {
      const { AddMeasurementModal } = await import("jobs/Job/MeasurementsTab/AddMeasurementModal");
      return { element: <AddMeasurementModal /> };
    },
    async loader(args) {
      const { addMeasurementLoader } = await import("src/router/loaders/jobs/Measurements");
      return addMeasurementLoader(args);
    },
    shouldRevalidate: () => false,
  },
  {
    path: "satellite_measure",
    async lazy() {
      const { SatelliteMeasureModal } = await import("jobs/Job/MeasurementsTab/SatelliteMeasureModal");
      return { element: <SatelliteMeasureModal /> };
    },
  },
  {
    path: "annotator",
    async loader(args) {
      const { annotatorLoader } = await import("src/router/loaders/jobs/Measurements");
      return annotatorLoader(args);
    },
    async lazy() {
      const { Annotator } = await import("jobs/Job/MeasurementsTab/Annotator");
      return { element: <Annotator /> };
    },
    // Re-run the loader ONLY when the image-picker query params change (folder navigation /
    // paging inside the wizard) — any broader predicate re-runs it on unrelated navigation and
    // risks disturbing wizard state. The wizard's step state is local to the component, so a
    // query-only loader re-run doesn't remount or reset it. A folder change carrying a stale
    // `page` is canonicalized inside `annotatorLoader` (redirect to page 1), so every delta can
    // safely revalidate.
    shouldRevalidate: ({ currentUrl, nextUrl }) =>
      ["folder_id", "page", "per_page"].some(
        (param) => currentUrl.searchParams.get(param) !== nextUrl.searchParams.get(param),
      ),
  },
];

// '/jobs/*/measurements'
export const MeasurementsRoutes: RouteObject[] = [
  {
    path: "",
    async loader(args) {
      const { measurementsTabLoader } = await import("src/router/loaders/Jobs");
      return measurementsTabLoader(args);
    },
    shouldRevalidate: () => false,
    async lazy() {
      const { MeasurementsTab } = await import("jobs/Job/MeasurementsTab");
      return { element: <MeasurementsTab /> };
    },
    children: [
      {
        path: "exterior",
        async lazy() {
          const { Exterior } = await import("jobs/Job/MeasurementsTab/Exterior");
          return { element: <Exterior /> };
        },
        children: [
          {
            async lazy() {
              const { Roofing } = await import("jobs/Job/MeasurementsTab/Exterior/Roofing");
              return { element: <Roofing /> };
            },
            path: "roofing",
            async loader(args) {
              const { measurementTypeLoader } = await import("src/router/loaders/jobs/Measurements");
              return measurementTypeLoader(args, "roofing");
            },
            children: MeasurementModalRoutes,
          },
          {
            async lazy() {
              const { Siding } = await import("jobs/Job/MeasurementsTab/Exterior/Siding");
              return { element: <Siding /> };
            },
            path: "siding",
            async loader(args) {
              const { measurementTypeLoader } = await import("src/router/loaders/jobs/Measurements");
              return measurementTypeLoader(args, "siding");
            },
            children: MeasurementModalRoutes,
          },
          {
            async lazy() {
              const { Walls } = await import("jobs/Job/MeasurementsTab/Exterior/Walls");
              return { element: <Walls /> };
            },
            path: "walls",
            async loader(args) {
              const { measurementTypeLoader } = await import("src/router/loaders/jobs/Measurements");
              return measurementTypeLoader(args, "walls");
            },
            children: MeasurementModalRoutes,
          },
          {
            async lazy() {
              const { Openings } = await import("jobs/Job/MeasurementsTab/Exterior/Openings");
              return { element: <Openings kind="window" /> };
            },
            path: "windows",
            async loader(args) {
              const { measurementTypeLoader } = await import("src/router/loaders/jobs/Measurements");
              return measurementTypeLoader(args, "windows");
            },
            children: MeasurementModalRoutes,
          },
          {
            async lazy() {
              const { Openings } = await import("jobs/Job/MeasurementsTab/Exterior/Openings");
              return { element: <Openings kind="door" /> };
            },
            path: "doors",
            async loader(args) {
              const { measurementTypeLoader } = await import("src/router/loaders/jobs/Measurements");
              return measurementTypeLoader(args, "doors");
            },
            children: MeasurementModalRoutes,
          },
          {
            element: (
              <div>
                Decks
                <Outlet />
              </div>
            ),
            path: "decks",
            async loader(args) {
              const { measurementTypeLoader } = await import("src/router/loaders/jobs/Measurements");
              return measurementTypeLoader(args, "decks");
            },
            children: MeasurementModalRoutes,
          },
          {
            async lazy() {
              const { Fencing } = await import("jobs/Job/MeasurementsTab/Fencing");
              return { element: <Fencing /> };
            },
            path: "fencing",
            async loader(args) {
              const { measurementTypeLoader } = await import("src/router/loaders/jobs/Measurements");
              return measurementTypeLoader(args, "fencing");
            },
            children: [
              {
                path: "",
                async loader(args) {
                  const { fencingLoader } = await import("src/router/loaders/jobs/Measurements");
                  return fencingLoader(args);
                },
                children: [
                  {
                    path: "fencing_tool",
                    async lazy() {
                      const { FencingModal } = await import("jobs/Job/MeasurementsTab/Fencing/FencingModal");
                      return { element: <FencingModal /> };
                    },
                  },
                  ...MeasurementModalRoutes,
                ],
              },
            ],
          },
          { path: "", element: <Redirect to="roofing" /> },
          { path: "*", element: <Redirect to="roofing" /> },
        ],
      },
      {
        path: "fencing_tool",
        async loader(args) {
          const { fencingLoader } = await import("src/router/loaders/jobs/Measurements");
          return fencingLoader(args, true);
        },
        async lazy() {
          const { FencingModal } = await import("jobs/Job/MeasurementsTab/Fencing/FencingModal");
          return { element: <FencingModal /> };
        },
      },
      {
        path: "interior",
        async loader(args) {
          const { interiorMeasurementsLoader } = await import("src/router/loaders/jobs/Measurements");
          return interiorMeasurementsLoader(args);
        },
        async lazy() {
          const { Interiors } = await import("jobs/Job/MeasurementsTab/Interiors");
          return { element: <Interiors /> };
        },
        children: [
          {
            path: ":roomId",
            async lazy() {
              const { Room } = await import("jobs/Job/MeasurementsTab/Interiors/Room");
              return { element: <Room /> };
            },
            children: MeasurementModalRoutes,
          },
        ],
      },
      {
        path: "",
        element: <Redirect to="exterior" />,
      },
      { path: "*", element: <Redirect to="exterior" /> },
    ],
  },
];
